"use client";

import React from "react";
import { ArrowRight, ShieldCheck, Building2, Briefcase, Layers3, FileText, Users, BadgeCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const useCases = [{
  id: "internal-knowledge",
  title: "Internal knowledge assistants",
  description: "Give teams grounded answers from approved company documents, policies, procedures, and internal knowledge without exposing raw or unreviewed content.",
  icon: Building2,
  link: "/SecurityAndGovernancePage",
  cta: "See Governance"
}, {
  id: "client-portals",
  title: "Client-facing knowledge portals",
  description: "Deploy retrieval-based AI experiences for customers or stakeholders where citations, controlled access, and clearer answer boundaries matter.",
  icon: Users,
  link: "/BookADemoContactSalesPage",
  cta: "Book a Demo"
}, {
  id: "agencies",
  title: "White-label agency deployments",
  description: "Agencies and resellers can launch branded RAG environments for clients without building a separate multi-tenant product themselves.",
  icon: Briefcase,
  link: "/WhiteLabelForAgenciesPage",
  cta: "Explore White-Label"
}, {
  id: "approved-knowledge",
  title: "Approved knowledge environments",
  description: "Use EloRAG where teams need answers only from approved knowledge, with refusal behavior when evidence is insufficient.",
  icon: BadgeCheck,
  link: "/SecurityAndGovernancePage",
  cta: "See Security"
}, {
  id: "citation-workflows",
  title: "Citation-based business workflows",
  description: "Support business use cases where answer traceability matters, including operational guidance, internal documentation, and controlled information access.",
  icon: FileText,
  link: "/SecurityAndGovernancePage",
  cta: "See How It Works"
}, {
  id: "multitenant-rag",
  title: "Multi-tenant RAG delivery",
  description: "Manage structured AI delivery across separate teams, tenants, or clients with stronger operational boundaries and clearer commercial control.",
  icon: Layers3,
  link: "/PricingPage",
  cta: "Explore Plans"
}];
export default function SectorSolutions() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="use-cases" className="overflow-hidden bg-secondary/20 py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div ref={headerRef} className={cn("mx-auto mb-12 max-w-3xl text-center transition-all duration-700 ease-out", isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")}>
          <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
            <ShieldCheck className="size-4" />
            <span>Where EloRAG Fits</span>
          </div>

          <h2 className="mb-5 text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-5xl">
            Use EloRAG across internal knowledge, client delivery, and white-label environments
          </h2>

          <p className="text-lg leading-relaxed text-muted-foreground">
            EloRAG fits business environments where grounded answers, approved-only retrieval,
            citations, and structured deployment matter more than lightweight chat-with-documents tools.
          </p>
        </div>

        <div ref={gridRef} className={cn("grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3 transition-all duration-700 ease-out", isGridVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0")}>
          {useCases.map((item, index) => {
          const Icon = item.icon;
          return <Card key={item.id} data-index={index} className="group h-full rounded-2xl border border-border/70 bg-card p-7 shadow-[0_12px_30px_-15px_rgba(0,0,0,0.12)] transition-all duration-300 hover:border-primary/25 hover:shadow-[0_18px_40px_-18px_rgba(0,0,0,0.15)]" style={{
            transitionDelay: `${index * 90}ms`
          }}>
                <div className="mb-5 flex items-center justify-between">
                  <div className="rounded-xl bg-primary/10 p-3">
                    <Icon className="size-5 text-primary" />
                  </div>
                </div>

                <h3 className="mb-3 text-xl font-semibold leading-tight text-foreground">
                  {item.title}
                </h3>

                <p className="mb-8 text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {item.description}
                </p>

                <div className="mt-auto">
                  <Link to={item.link} className="group/link inline-flex items-center font-medium text-primary transition-colors hover:text-primary/80">
                    {item.cta}
                    <ArrowRight className="ml-2 size-4 transition-transform group-hover/link:translate-x-1" />
                  </Link>
                </div>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}